// ── Chat System ── Text chat and radio command overlay
// Handles team/all text chat with message display, input, and radio commands.

import { isMobile } from '../core/device';

export const CHAT_MAX_LENGTH = 200;
export const CHAT_FADE_MS = 10_000;
const CHAT_MAX_VISIBLE = 8;
const CHAT_RATE_LIMIT_MS = 1_000; // 1 message per second (client-side guard)
const CHAT_SHORTCUTS: Record<string, string> = {
  ':gg': 'GG',
  ':gl': 'GL',
  ':nice': 'Nice!',
  ':wp': 'Well played!',
};

// ── Types ──

export interface ChatMessage {
  playerId: number;
  playerName: string;
  channel: 'team' | 'all';
  message: string;
  timestamp: number;
  isRadio?: boolean;
}

export interface ValidationResult {
  valid: boolean;
  reason?: string;
}

// ── Radio Commands ──

export const RADIO_COMMANDS: Record<'z' | 'x' | 'c', string[]> = {
  z: ['Cover me', 'You take the point', 'Hold this position', 'Regroup', 'Follow me'],
  x: ['Go go go', 'Fall back', 'Stick together', 'Get in position', 'Storm the front'],
  c: ['Enemy spotted', 'Need backup', 'Sector clear', "I'm in position", 'Negative'],
};

type RadioMenuKey = 'z' | 'x' | 'c';

// ── Pure helpers (tested) ──

export function formatChatMessage(msg: ChatMessage): string {
  if (msg.isRadio) {
    return `[RADIO] ${msg.playerName}: ${msg.message}`;
  }
  const prefix = msg.channel === 'team' ? '[TEAM]' : '[ALL]';
  return `${prefix} ${msg.playerName}: ${msg.message}`;
}

export function validateChatMessage(text: string): ValidationResult {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return { valid: false, reason: 'Message is empty' };
  }
  if (trimmed.length > CHAT_MAX_LENGTH) {
    return { valid: false, reason: `Message exceeds ${CHAT_MAX_LENGTH} characters` };
  }
  return { valid: true };
}

export function getRadioCommand(menuKey: string, number: number): string | null {
  if (menuKey !== 'z' && menuKey !== 'x' && menuKey !== 'c') return null;
  if (number < 1 || number > 5) return null;
  return RADIO_COMMANDS[menuKey as RadioMenuKey][number - 1];
}

function expandChatShortcut(text: string): string {
  const lower = text.toLowerCase();
  for (const [shortcut, replacement] of Object.entries(CHAT_SHORTCUTS)) {
    if (lower === shortcut || lower.startsWith(`${shortcut} `)) {
      return `${replacement}${text.slice(shortcut.length)}`;
    }
  }
  return text;
}

// ── Chat UI Class ──

export type ChatSendCallback = (channel: 'team' | 'all', message: string) => void;
export type RadioSendCallback = (command: string) => void;

export class ChatUI {
  private container: HTMLElement;
  private channelToggle: HTMLElement;
  private messageList: HTMLElement;
  private inputWrapper: HTMLElement;
  private inputEl: HTMLInputElement;
  private messages: Array<ChatMessage & { addedAt: number }> = [];
  private chatOpen = false;
  private channel: 'team' | 'all' = 'team';
  private lastSendTime = 0;
  private onChatSend: ChatSendCallback;
  private onRadioSend: RadioSendCallback;
  private fadeInterval: ReturnType<typeof setInterval> | null = null;
  private renderPending = false;

  // Radio menu state
  private radioMenuOpen = false;
  private radioMenuKey: RadioMenuKey | null = null;
  private radioOverlay: HTMLElement;
  // Mobile tap-to-select radio wheel (all 3 rows visible)
  private radioWheelOpen = false;
  private radioWheelBackdrop: HTMLElement;
  private radioWheelPanel: HTMLElement;

  constructor(onChatSend: ChatSendCallback, onRadioSend: RadioSendCallback) {
    this.onChatSend = onChatSend;
    this.onRadioSend = onRadioSend;

    // ── Chat container (bottom-left) ──
    // BUG-HUD-10: the 420px container was wider than most phones and overlapped
    // the bottom-left joystick zone (40% × 50% of screen). On mobile, lift it
    // above the joystick (bottom: 220px ish, plus the iOS home-indicator inset)
    // and cap width to 90vw so it never overflows the viewport.
    const mobile = isMobile();
    this.container = document.createElement('div');
    this.container.id = 'chat-container';
    Object.assign(this.container.style, {
      position: 'fixed',
      bottom: mobile
        ? 'calc(220px + env(safe-area-inset-bottom, 0px))'
        : '80px',
      left: mobile ? '8px' : '16px',
      width: 'min(420px, 90vw)',
      zIndex: '50',
      pointerEvents: 'none',
      fontFamily: '"Courier New", monospace',
    });
    document.body.appendChild(this.container);

    this.channelToggle = document.createElement('div');
    this.channelToggle.id = 'chat-channel-toggle';
    Object.assign(this.channelToggle.style, {
      display: 'none',
      alignSelf: 'flex-start',
      gap: '2px',
      marginBottom: '6px',
      padding: '2px',
      background: 'rgba(0, 0, 0, 0.75)',
      border: '1px solid rgba(255, 255, 255, 0.12)',
      borderRadius: '999px',
      pointerEvents: 'auto',
    });
    this.container.appendChild(this.channelToggle);

    // Message list
    this.messageList = document.createElement('div');
    this.messageList.id = 'chat-messages';
    Object.assign(this.messageList.style, {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      marginBottom: '6px',
    });
    this.container.appendChild(this.messageList);

    // Input wrapper (hidden by default)
    this.inputWrapper = document.createElement('div');
    this.inputWrapper.id = 'chat-input-wrapper';
    Object.assign(this.inputWrapper.style, {
      display: 'none',
      background: 'rgba(0, 0, 0, 0.75)',
      borderRadius: '4px',
      padding: '6px 10px',
      pointerEvents: 'auto',
    });
    this.container.appendChild(this.inputWrapper);

    // Channel label
    const channelLabel = document.createElement('span');
    channelLabel.id = 'chat-channel-label';
    Object.assign(channelLabel.style, {
      color: '#888',
      fontSize: '12px',
      marginRight: '8px',
    });
    channelLabel.textContent = '[TEAM]';
    this.inputWrapper.appendChild(channelLabel);

    // Input field
    this.inputEl = document.createElement('input');
    this.inputEl.id = 'chat-input';
    this.inputEl.type = 'text';
    this.inputEl.maxLength = CHAT_MAX_LENGTH;
    this.inputEl.placeholder = 'Type a message...';
    Object.assign(this.inputEl.style, {
      background: 'transparent',
      border: 'none',
      outline: 'none',
      color: '#ffffff',
      fontSize: '13px',
      fontFamily: '"Courier New", monospace',
      width: '340px',
    });
    this.inputWrapper.appendChild(this.inputEl);
    this.renderChannelToggle();

    // ── Radio overlay ──
    this.radioOverlay = document.createElement('div');
    this.radioOverlay.id = 'radio-menu';
    Object.assign(this.radioOverlay.style, {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      background: 'rgba(0, 0, 0, 0.85)',
      border: '1px solid #444',
      borderRadius: '6px',
      padding: '12px 20px',
      zIndex: '65',
      display: 'none',
      fontFamily: '"Courier New", monospace',
      color: '#ffffff',
      fontSize: '14px',
      minWidth: '220px',
      pointerEvents: 'none',
    });
    document.body.appendChild(this.radioOverlay);

    // ── Mobile radio wheel (all 3 rows, tap-to-select) ──
    // Backdrop sits below the panel so tapping outside dismisses without
    // intercepting button clicks on the panel itself.
    this.radioWheelBackdrop = document.createElement('div');
    this.radioWheelBackdrop.id = 'radio-wheel-backdrop';
    Object.assign(this.radioWheelBackdrop.style, {
      position: 'fixed',
      inset: '0',
      background: 'rgba(0, 0, 0, 0.6)',
      zIndex: '60',
      display: 'none',
      pointerEvents: 'auto',
    });
    this.radioWheelBackdrop.addEventListener('click', () => this.closeRadioMenu());
    document.body.appendChild(this.radioWheelBackdrop);

    this.radioWheelPanel = document.createElement('div');
    this.radioWheelPanel.id = 'radio-wheel-panel';
    Object.assign(this.radioWheelPanel.style, {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      background: 'rgba(26, 26, 46, 0.98)',
      border: '2px solid rgba(233, 69, 96, 0.4)',
      borderRadius: '8px',
      padding: '14px',
      zIndex: '65',
      display: 'none',
      fontFamily: '"Courier New", monospace',
      color: '#ffffff',
      width: 'min(560px, 92vw)',
      maxHeight: '90vh',
      overflowY: 'auto',
      pointerEvents: 'auto',
      boxSizing: 'border-box',
    });
    // Stop clicks on the panel from bubbling to the backdrop (which closes).
    this.radioWheelPanel.addEventListener('click', (e) => e.stopPropagation());
    document.body.appendChild(this.radioWheelPanel);

    // Input event handlers
    this.inputEl.addEventListener('keydown', (e) => this.handleInputKey(e));

    // Start fade ticker
    this.fadeInterval = setInterval(() => this.pruneExpiredMessages(), 1_000);
  }

  // ── Public API ──

  /** Whether the chat input or radio menu is currently open. */
  isOpen(): boolean {
    return this.chatOpen || this.radioMenuOpen || this.radioWheelOpen;
  }

  /** Open the chat input for a specific channel. */
  open(channel: 'team' | 'all'): void {
    if (this.chatOpen) return;
    this.chatOpen = true;
    this.channel = channel;
    this.channelToggle.style.display = 'flex';
    this.inputWrapper.style.display = 'flex';
    this.inputEl.value = '';
    this.updateChannelUi();

    // Focus with a microtask delay to prevent the Enter key from
    // immediately closing the input
    requestAnimationFrame(() => this.inputEl.focus());
  }

  /** Close the chat input. */
  close(): void {
    this.chatOpen = false;
    this.channelToggle.style.display = 'none';
    this.inputWrapper.style.display = 'none';
    this.inputEl.blur();
  }

  /** Add a message to the display (received from server or local). */
  addMessage(msg: ChatMessage): void {
    const entry = { ...msg, addedAt: performance.now() };
    this.messages.push(entry);

    // Keep only last N messages
    if (this.messages.length > CHAT_MAX_VISIBLE) {
      this.messages.shift();
    }

    // Batch rapid messages into a single rAF to avoid render spam
    if (!this.renderPending) {
      this.renderPending = true;
      requestAnimationFrame(() => {
        this.renderPending = false;
        this.renderMessages();
      });
    }
  }

  /**
   * U10: return the current chat history as plain strings using the canonical
   * `formatChatMessage` formatter. Used by spectator transcript exports and
   * end-of-match summary surfaces, and provides a single external consumer of
   * the helper alongside the in-module `renderMessages` call. Newest-last.
   */
  getFormattedHistory(): string[] {
    return this.messages.map((m) => formatChatMessage(m));
  }

  /**
   * Open the radio menu.
   *  - With `menuKey` (z/x/c): legacy desktop behaviour — show a single
   *    submenu listing 5 lines, picked via number keys 1-5.
   *  - Without args: BUG-PARITY-H2 mobile wheel — show all three rows
   *    (COMMAND / STANDARD / REPORT) with tappable buttons. Used by the
   *    mobile touch HUD where number-key input isn't available.
   */
  openRadioMenu(menuKey?: RadioMenuKey): void {
    if (this.chatOpen) return;
    if (menuKey) {
      // Close any open wheel before showing the legacy single-submenu.
      if (this.radioWheelOpen) this.closeRadioWheelOnly();
      this.radioMenuOpen = true;
      this.radioMenuKey = menuKey;
      this.renderRadioMenu();
      this.radioOverlay.style.display = 'block';
      return;
    }
    // Mobile tap-to-select wheel — close legacy submenu if it was up.
    if (this.radioMenuOpen) {
      this.radioMenuOpen = false;
      this.radioMenuKey = null;
      this.radioOverlay.style.display = 'none';
    }
    this.radioWheelOpen = true;
    this.renderRadioWheel();
    this.radioWheelBackdrop.style.display = 'block';
    this.radioWheelPanel.style.display = 'block';
  }

  /** Close the radio submenu (or the mobile wheel). */
  closeRadioMenu(): void {
    this.radioMenuOpen = false;
    this.radioMenuKey = null;
    this.radioOverlay.style.display = 'none';
    this.closeRadioWheelOnly();
  }

  private closeRadioWheelOnly(): void {
    this.radioWheelOpen = false;
    this.radioWheelBackdrop.style.display = 'none';
    this.radioWheelPanel.style.display = 'none';
  }

  /** Handle a number key press while radio menu is open. Returns true if handled. */
  handleRadioSelect(num: number): boolean {
    if (!this.radioMenuOpen || !this.radioMenuKey) return false;
    const cmd = getRadioCommand(this.radioMenuKey, num);
    if (!cmd) return false;

    this.onRadioSend(cmd);
    this.closeRadioMenu();
    return true;
  }

  /** Handle a key event for the radio menu or chat opening. Returns true if consumed. */
  handleKeyDown(e: KeyboardEvent): boolean {
    // Mobile wheel: Escape closes; otherwise let other handlers run.
    if (this.radioWheelOpen) {
      if (e.code === 'Escape') {
        this.closeRadioMenu();
        return true;
      }
      // Don't consume — keep it tap-only so it doesn't fight with movement.
      return false;
    }

    // If radio menu is open, handle number keys or escape
    if (this.radioMenuOpen) {
      if (e.code === 'Escape') {
        this.closeRadioMenu();
        return true;
      }
      const num = parseInt(e.key);
      if (num >= 1 && num <= 5) {
        return this.handleRadioSelect(num);
      }
      // Any other key closes radio menu
      this.closeRadioMenu();
      return true;
    }

    // Chat input is open — all keys are consumed
    if (this.chatOpen) {
      return true;
    }

    // Open radio menu with Z, X, C
    if (e.code === 'KeyZ') {
      this.openRadioMenu('z');
      return true;
    }
    if (e.code === 'KeyX') {
      this.openRadioMenu('x');
      return true;
    }
    if (e.code === 'KeyC') {
      this.openRadioMenu('c');
      return true;
    }

    // Enter = team chat, Shift+Enter = all chat
    if (e.code === 'Enter') {
      const channel = e.shiftKey ? 'all' : 'team';
      this.open(channel);
      return true;
    }

    return false;
  }

  dispose(): void {
    this.radioMenuOpen = false;
    this.radioWheelOpen = false;
    this.chatOpen = false;
    if (this.fadeInterval) {
      clearInterval(this.fadeInterval);
      this.fadeInterval = null;
    }
    this.container.remove();
    this.radioOverlay.remove();
    this.radioWheelBackdrop.remove();
    this.radioWheelPanel.remove();
  }

  // ── Private ──

  private handleInputKey(e: KeyboardEvent): void {
    e.stopPropagation(); // Prevent game input handlers from seeing these keys

    if (e.code === 'Escape') {
      this.close();
      return;
    }

    if (e.code === 'Enter') {
      const text = expandChatShortcut(this.inputEl.value);
      const validation = validateChatMessage(text);

      if (validation.valid) {
        const now = Date.now();
        if (now - this.lastSendTime >= CHAT_RATE_LIMIT_MS) {
          this.onChatSend(this.channel, text);
          this.lastSendTime = now;
        }
      }
      this.close();
      return;
    }
  }

  private renderMessages(): void {
    this.messageList.innerHTML = '';
    const now = performance.now();

    for (const msg of this.messages) {
      const age = now - msg.addedAt;
      const opacity = age > CHAT_FADE_MS ? 0 : age > CHAT_FADE_MS - 2_000 ? (CHAT_FADE_MS - age) / 2_000 : 1;
      if (opacity <= 0) continue;

      const el = document.createElement('div');
      Object.assign(el.style, {
        padding: '2px 8px',
        background: 'rgba(0, 0, 0, 0.55)',
        borderRadius: '3px',
        fontSize: '13px',
        opacity: String(opacity),
        transition: 'opacity 0.5s',
      });

      // Color based on channel / radio
      let color = '#ffffff';
      if (msg.isRadio) {
        color = '#ffcc00';
      } else if (msg.channel === 'team') {
        // Team color — T=orange, CT=blue. We don't know team here, so use a neutral gold.
        color = '#e8b849';
      }

      el.style.color = color;
      el.textContent = formatChatMessage(msg);
      this.messageList.appendChild(el);
    }
  }

  private renderChannelToggle(): void {
    this.channelToggle.innerHTML = '';
    for (const value of ['all', 'team'] as const) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.dataset.channel = value;
      btn.textContent = value === 'all' ? 'All' : 'Team';
      Object.assign(btn.style, {
        border: '0',
        borderRadius: '999px',
        padding: '4px 10px',
        background: 'transparent',
        color: '#888',
        cursor: 'pointer',
        fontSize: '11px',
        fontFamily: '"Courier New", monospace',
        textTransform: 'uppercase',
        letterSpacing: '1px',
      });
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.channel = value;
        this.updateChannelUi();
        if (this.chatOpen) this.inputEl.focus();
      });
      this.channelToggle.appendChild(btn);
    }
    this.updateChannelUi();
  }

  private updateChannelUi(): void {
    const label = this.inputWrapper.querySelector('#chat-channel-label') as HTMLElement | null;
    if (label) {
      label.textContent = this.channel === 'team' ? '[TEAM]' : '[ALL]';
      label.style.color = this.channel === 'team' ? '#e8b849' : '#cccccc';
    }

    const buttons = this.channelToggle.querySelectorAll<HTMLButtonElement>('button[data-channel]');
    for (const btn of buttons) {
      const selected = btn.dataset.channel === this.channel;
      btn.style.background = selected ? (this.channel === 'team' ? 'rgba(232, 184, 73, 0.22)' : 'rgba(204, 204, 204, 0.18)') : 'transparent';
      btn.style.color = selected ? '#fff' : '#888';
    }
  }

  private pruneExpiredMessages(): void {
    const now = performance.now();
    const had = this.messages.length;
    this.messages = this.messages.filter((m) => now - m.addedAt < CHAT_FADE_MS + 2_000);
    if (this.messages.length !== had) {
      this.renderMessages();
    }
  }

  private renderRadioMenu(): void {
    if (!this.radioMenuKey) return;
    const cmds = RADIO_COMMANDS[this.radioMenuKey];
    const menuNames: Record<RadioMenuKey, string> = {
      z: 'COMMANDS',
      x: 'STANDARD RADIO',
      c: 'REPORT RADIO',
    };

    this.radioOverlay.innerHTML = '';

    const title = document.createElement('div');
    Object.assign(title.style, {
      fontSize: '12px',
      color: '#e94560',
      fontWeight: 'bold',
      letterSpacing: '2px',
      marginBottom: '8px',
      textTransform: 'uppercase',
    });
    title.textContent = menuNames[this.radioMenuKey];
    this.radioOverlay.appendChild(title);

    for (let i = 0; i < cmds.length; i++) {
      const row = document.createElement('div');
      Object.assign(row.style, {
        padding: '4px 0',
        color: '#cccccc',
      });
      row.textContent = `${i + 1}. ${cmds[i]}`;
      this.radioOverlay.appendChild(row);
    }

    const hint = document.createElement('div');
    Object.assign(hint.style, {
      marginTop: '8px',
      fontSize: '11px',
      color: '#555',
    });
    hint.textContent = 'Press 1-5 or ESC to cancel';
    this.radioOverlay.appendChild(hint);
  }

  private renderRadioWheel(): void {
    const rowLabels: Record<RadioMenuKey, string> = {
      z: 'COMMAND',
      x: 'STANDARD',
      c: 'REPORT',
    };

    this.radioWheelPanel.innerHTML = '';

    // Title bar
    const title = document.createElement('div');
    Object.assign(title.style, {
      fontSize: '13px',
      color: '#e94560',
      fontWeight: 'bold',
      letterSpacing: '2px',
      marginBottom: '10px',
      textTransform: 'uppercase',
      textAlign: 'center',
    });
    title.textContent = 'Radio';
    this.radioWheelPanel.appendChild(title);

    for (const key of ['z', 'x', 'c'] as const) {
      const row = document.createElement('div');
      Object.assign(row.style, {
        marginBottom: '10px',
      });

      const label = document.createElement('div');
      Object.assign(label.style, {
        fontSize: '11px',
        color: '#888',
        letterSpacing: '1px',
        marginBottom: '4px',
        textTransform: 'uppercase',
      });
      label.textContent = `${rowLabels[key]} (${key.toUpperCase()})`;
      row.appendChild(label);

      const btnRow = document.createElement('div');
      Object.assign(btnRow.style, {
        display: 'grid',
        // Stack to 2 cols on narrow screens; 5 cols on roomier viewports.
        gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))',
        gap: '6px',
      });

      const cmds = RADIO_COMMANDS[key];
      for (let i = 0; i < cmds.length; i++) {
        const cmd = cmds[i];
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.dataset.radioCategory = key;
        btn.dataset.radioLine = String(i + 1);
        Object.assign(btn.style, {
          minHeight: '44px',
          padding: '8px 10px',
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(233, 69, 96, 0.4)',
          borderRadius: '4px',
          color: '#ffffff',
          fontFamily: '"Courier New", monospace',
          fontSize: '13px',
          cursor: 'pointer',
          textAlign: 'center',
          lineHeight: '1.2',
        });
        btn.textContent = cmd;
        btn.addEventListener('click', (ev) => {
          ev.stopPropagation();
          this.onRadioSend(cmd);
          this.closeRadioMenu();
        });
        btnRow.appendChild(btn);
      }

      row.appendChild(btnRow);
      this.radioWheelPanel.appendChild(row);
    }

    const hint = document.createElement('div');
    Object.assign(hint.style, {
      marginTop: '4px',
      fontSize: '11px',
      color: '#666',
      textAlign: 'center',
    });
    hint.textContent = 'Tap a line — tap outside to cancel';
    this.radioWheelPanel.appendChild(hint);
  }
}
